import { useRef, useState } from 'react';
import type { OverlayNote } from '@srcbattle/shared';
import { Button } from './ui.tsx';

/**
 * The operator's text overlay.
 *
 * Notes the person running the night puts on screen — "BREAK — back in 5", a
 * round title, a joke. Everyone sees them in the same place, which is the only
 * reason they are server state rather than a sticky note on a monitor.
 *
 * Two constraints shape this, and both come from the plan:
 *
 * **Percentages, not pixels** (§8.5). People are on different monitors and on
 * phones. A note dragged to x=900px on a wide monitor is off-screen for half
 * the room, so position and width are stored as a share of the viewport and
 * rendered the same way.
 *
 * The layer spans the viewport. Operators can therefore place a lower-third,
 * title card, or side label instead of being trapped in a narrow strip.
 */
export function OverlayNotes({
  notes,
  canEdit,
  onMove,
  onRemove,
}: {
  notes: OverlayNote[];
  canEdit: boolean;
  onMove: (noteId: string, patch: { xPct?: number; yPct?: number; fontScale?: number }) => void;
  onRemove: (noteId: string) => void;
}) {
  const areaRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, { xPct: number; yPct: number }>>({});

  if (notes.length === 0) return null;

  const startDrag = (note: OverlayNote) => (event: React.PointerEvent) => {
    if (!canEdit) return;
    event.preventDefault();
    setDragging(note.id);

    const area = areaRef.current;
    if (!area) return;

    let latest = { xPct: note.xPct, yPct: note.yPct };
    const move = (moveEvent: PointerEvent) => {
      const box = area.getBoundingClientRect();
      // Clamped so a note can always be dragged back: dropping one at 100%
      // would put its own grab handle off the edge of the strip.
      const xPct = Math.min(90, Math.max(0, ((moveEvent.clientX - box.left) / box.width) * 100));
      const yPct = Math.min(92, Math.max(0, ((moveEvent.clientY - box.top) / box.height) * 100));
      latest = { xPct, yPct };
      // Movement is deliberately local. Sending every pointer event made
      // everyone else's note jitter in real time and needlessly flooded the
      // room; the settled position is published once on release.
      setDrafts((current) => ({ ...current, [note.id]: latest }));
    };

    const end = () => {
      setDragging(null);
      onMove(note.id, latest);
      setDrafts((current) => {
        const next = { ...current };
        delete next[note.id];
        return next;
      });
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
  };

  return (
    <div
      ref={areaRef}
      className="pointer-events-none fixed inset-0 z-40"
    >
      {notes.map((note) => (
        (() => {
          const position = drafts[note.id] ?? note;
          return (
        <div
          key={note.id}
          style={{
            left: `${position.xPct}%`,
            top: `${position.yPct}%`,
            width: `${note.wPct}%`,
            fontSize: `${note.fontScale}rem`,
          }}
          className={`pointer-events-auto absolute rounded-lg border border-accent/30 bg-surface/90 px-2.5 py-1.5 leading-tight text-accent shadow-lg backdrop-blur ${
            canEdit ? 'cursor-grab select-none touch-none' : ''
          } ${dragging === note.id ? 'cursor-grabbing ring-2 ring-accent' : ''}`}
          onPointerDown={startDrag(note)}
        >
          <span className="break-words">{note.text}</span>
          {canEdit && (
            <div className="absolute -top-7 left-0 flex overflow-hidden rounded-md border border-border bg-surface text-xs shadow">
              <button type="button" aria-label="Smaller text" className="px-2 py-1 hover:bg-surface-raised" onPointerDown={(event) => event.stopPropagation()} onClick={() => onMove(note.id, { fontScale: Math.max(.5, note.fontScale - .25) })}>A−</button>
              <button type="button" aria-label="Larger text" className="px-2 py-1 hover:bg-surface-raised" onPointerDown={(event) => event.stopPropagation()} onClick={() => onMove(note.id, { fontScale: Math.min(3, note.fontScale + .25) })}>A+</button>
              <button
              type="button"
              aria-label="Remove note"
              onPointerDown={(event) => event.stopPropagation()}
              onClick={() => onRemove(note.id)}
              className="px-2 py-1 text-text-faint hover:bg-surface-raised hover:text-text"
            >
              ×
            </button>
            </div>
          )}
        </div>
          );
        })()
      ))}
    </div>
  );
}

export function OverlayComposer({
  busy,
  onAdd,
  onClear,
  hasNotes,
}: {
  busy: boolean;
  onAdd: (text: string) => void;
  onClear: () => void;
  hasNotes: boolean;
}) {
  const [text, setText] = useState('');

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        const trimmed = text.trim();
        if (!trimmed) return;
        onAdd(trimmed);
        setText('');
      }}
      className="flex flex-wrap items-center gap-2"
    >
      <input
        value={text}
        onChange={(event) => setText(event.target.value)}
        maxLength={140}
        placeholder="BREAK — back in 5"
        aria-label="Overlay note"
        className="min-w-0 flex-1 rounded-lg border border-border-strong bg-surface px-3 py-1.5 text-sm"
      />
      <Button type="submit" disabled={busy || !text.trim()}>
        Put it up
      </Button>
      {hasNotes && (
        <Button variant="ghost" onClick={onClear} disabled={busy}>
          Clear all
        </Button>
      )}
    </form>
  );
}
